import { isFieldWithPathDirty } from '@occc-app/offerHelpers/utils';
import { makeDeleteOperation } from '@occc-app/offerHelpers/operations/makeDeleteOperation';
import { makeEarningMechanismCreateOperation } from '@occc-app/offerHelpers/operations/earningMechanism';
import { EarningMechanismConfigurationInput, ExternalAwardProvider } from '@occc/graphql';
import { AwardType, DirtyFieldsType, Operation } from '@occc-app/offerHelpers/types';
import { earningMechanismInputCreator } from '@occc-app/offerHelpers/earningMechanismInputCreator';
import { awardInputCreator } from '@occc-app/offerHelpers/awardInputCreator';
import {
  getAwardInput,
  getAwardPointsOrVouchersOrNullInput,
} from '@occc-app/offerHelpers/utils/getAwardInput';

import { OfferConfigureFormValues, OfferType } from '../../../types';

export type EarningMechanismCreateInput = Pick<
  EarningMechanismConfigurationInput,
  | 'productPurchase'
  | 'totalSpendOverTime'
  | 'totalSpendOnTransaction'
  | 'totalEligiblePurchase'
  | 'externalFulfillment'
>;

type GetEarningMechanismCreateInput = (
  values: OfferConfigureFormValues,
) => EarningMechanismCreateInput;

export const getEarningMechanismCreateInput: GetEarningMechanismCreateInput = (values) => {
  const { offerType } = values;
  const productPurchase = 'productPurchase' in values ? values.productPurchase : undefined;
  const spendBonus = 'spendBonus' in values ? values.spendBonus : undefined;
  const baseEarn = 'baseEarn' in values ? values.baseEarn : undefined;
  const baseEligibleEarn = 'baseEligibleEarn' in values ? values.baseEligibleEarn : undefined;

  if (offerType === OfferType.ProductPurchase && productPurchase) {
    const { eligibleProductGroups, purchaseThreshold, countProductsBy } = productPurchase;
    const awardInput = getAwardInput({
      awardType: values.awardType,
      awardVouchers:
        values.awardType === AwardType.AwardVouchers ? values.awardVouchers : undefined,
      awardPoints: values.awardType === AwardType.AwardPoints ? values.awardPoints : undefined,
      awardPointsPer: undefined,
      // When an external award is added, the provider should be “unknown” according to OCST-19711
      awardExternal: { provider: ExternalAwardProvider.Unknown },
    });

    return earningMechanismInputCreator.createProductPurchase(
      purchaseThreshold,
      eligibleProductGroups,
      awardInput,
      countProductsBy,
    );
  }

  if (offerType === OfferType.SpendBonus && spendBonus) {
    const { ineligibleProductGroups, accumulationThreshold, stayVisibleWhileInProgress } =
      spendBonus;
    const awardInput = getAwardInput({
      awardType: values.awardType,
      awardVouchers:
        values.awardType === AwardType.AwardVouchers ? values.awardVouchers : undefined,
      awardPoints: values.awardType === AwardType.AwardPoints ? values.awardPoints : undefined,
      awardPointsPer: undefined,
    });

    return earningMechanismInputCreator.createTotalSpendOverTime(
      accumulationThreshold,
      ineligibleProductGroups,
      awardInput,
      stayVisibleWhileInProgress?.isEnabled,
    );
  }

  if (
    offerType === OfferType.BaseEarn &&
    baseEarn &&
    values.awardType === AwardType.AwardPointsPer
  ) {
    const { awardPointsPer } = values;

    const { roundMoneyAmount, ineligibleProductGroups } = baseEarn;
    const award = awardInputCreator.createAwardPointsPer(
      awardPointsPer.points,
      awardPointsPer.rounding,
    );

    return earningMechanismInputCreator.createTotalSpendOnTransaction(
      roundMoneyAmount,
      ineligibleProductGroups,
      award.awardPointsPer,
    );
  }

  if (
    offerType === OfferType.BaseEligibleEarn &&
    baseEligibleEarn &&
    values.awardType === AwardType.AwardPointsPer
  ) {
    const { awardPointsPer } = values;

    const { countProductsBy, roundCountedProducts, eligibleProductGroups } = baseEligibleEarn;
    const award = awardInputCreator.createAwardPointsPer(
      awardPointsPer.points,
      awardPointsPer.rounding,
    );

    return earningMechanismInputCreator.createTotalEligiblePurchase(
      countProductsBy,
      roundCountedProducts,
      eligibleProductGroups,
      award.awardPointsPer,
    );
  }

  if (offerType === OfferType.ExternalFulfillment) {
    const awardInput = getAwardPointsOrVouchersOrNullInput({
      awardType: values.awardType,
      awardPoints: values.awardType === AwardType.AwardPoints ? values.awardPoints : undefined,
      awardVouchers:
        values.awardType === AwardType.AwardVouchers ? values.awardVouchers : undefined,
    });

    return earningMechanismInputCreator.createExternalFulfillment(awardInput);
  }

  throw new Error(`${offerType} offer type or its configuration is not supported or not provided`);
};

export const getEarningMechanismOperations = (
  offerId: string,
  values: OfferConfigureFormValues,
  dirtyFields: DirtyFieldsType,
): Operation[] => {
  const operations: Operation[] = [];

  if (
    !isFieldWithPathDirty(dirtyFields, 'offerType') &&
    !isFieldWithPathDirty(dirtyFields, 'productPurchase') &&
    !isFieldWithPathDirty(dirtyFields, 'spendBonus') &&
    !isFieldWithPathDirty(dirtyFields, 'baseEarn') &&
    !isFieldWithPathDirty(dirtyFields, 'baseEligibleEarn') &&
    !isFieldWithPathDirty(dirtyFields, 'awardType') &&
    !isFieldWithPathDirty(dirtyFields, 'awardPoints') &&
    !isFieldWithPathDirty(dirtyFields, 'awardPointsPer') &&
    !isFieldWithPathDirty(dirtyFields, 'awardVouchers')
  ) {
    return [];
  }

  if (values.earningMechanism.id) {
    operations.push(makeDeleteOperation('earningMechanismDelete', values.earningMechanism.id));
  }

  // TODO: it's possible that these checks are not needed at all
  // we need to review types and double-check that again
  // [start]
  if (!values.offerType) {
    throw new Error('OfferType is not defined');
  }
  // [end]

  const configuration = getEarningMechanismCreateInput(values);
  operations.push(makeEarningMechanismCreateOperation(offerId, configuration));

  return operations;
};
